import axios from 'axios';
import { DeliveryEventsQuery, IDeliveryDashboardRepository } from '../../application/ports/IDeliveryDashboardRepository';
import {
  DeliveryAlerts,
  DeliveryEventDetail,
  DeliveryEventsPage,
  DeliveryMatrixGroupBy,
  DeliveryMatrixRow,
  DeliverySummary,
  DeliveryWindow,
} from '../../domain/entities/DeliveryDashboard';
import { advertizerApiHost } from '../../constants/envs';
import { AuthTokenProvider } from '../auth/AuthTokenProvider';

const BASE_PATH = '/api/admin/deliveries';

const windowParams = (window: DeliveryWindow) => ({ from: window.from.toISOString(), to: window.to.toISOString() });

export class DeliveryDashboardAxiosRepository implements IDeliveryDashboardRepository {
  constructor(private auth: AuthTokenProvider) {}

  private async authHeaders() {
    const token = await this.auth.getToken();
    if (!token) throw new Error('Unauthenticated');
    return { Authorization: `Bearer ${token}` };
  }

  async summary(window: DeliveryWindow): Promise<DeliverySummary> {
    const headers = await this.authHeaders();
    const res = await axios.get(`${advertizerApiHost}${BASE_PATH}/summary`, { headers, params: windowParams(window) });
    return { past: res.data.past ?? {}, future: res.data.future ?? {}, topErrors: res.data.topErrors ?? [] };
  }

  async alerts(window: DeliveryWindow): Promise<DeliveryAlerts> {
    const headers = await this.authHeaders();
    const res = await axios.get(`${advertizerApiHost}${BASE_PATH}/alerts`, { headers, params: windowParams(window) });
    return { delayed: res.data.delayed ?? 0, blockedTargets: res.data.blockedTargets ?? [], failingAccounts: res.data.failingAccounts ?? [] };
  }

  async matrix(window: DeliveryWindow, groupBy: DeliveryMatrixGroupBy, timezone: string): Promise<DeliveryMatrixRow[]> {
    const headers = await this.authHeaders();
    const res = await axios.get(`${advertizerApiHost}${BASE_PATH}/matrix`, { headers, params: { ...windowParams(window), groupBy, timezone } });
    return res.data.rows ?? [];
  }

  async events(query: DeliveryEventsQuery): Promise<DeliveryEventsPage> {
    const headers = await this.authHeaders();
    const params: Record<string, string | number> = { ...windowParams(query.window), page: query.page, perPage: query.perPage };
    if (query.filters.outcomes.length) params.outcomes = query.filters.outcomes.join(',');
    if (query.filters.telegramAccountUuid) params.telegramAccountUuid = query.filters.telegramAccountUuid;
    if (query.filters.channelUuid) params.channelUuid = query.filters.channelUuid;
    if (query.filters.errorCode) params.errorCode = query.filters.errorCode;
    const res = await axios.get(`${advertizerApiHost}${BASE_PATH}`, { headers, params });
    return { events: res.data.events ?? [], total: res.data.pagination?.total ?? 0 };
  }

  async event(uuid: string): Promise<DeliveryEventDetail> {
    const headers = await this.authHeaders();
    const res = await axios.get(`${advertizerApiHost}${BASE_PATH}/${uuid}`, { headers });
    return { event: res.data.event, attempts: res.data.attempts ?? [] };
  }
}
